import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { boughtTogetherProducts } from '../../../mocks/product';
import { productDetail } from '../../../mocks/product';
import { useCart } from '@/hooks/useCart';

interface BundleProduct {
  id: number;
  name: string;
  price: number;
  oldPrice: number;
  rating: number;
  reviews: number;
  color: string;
  description: string;
  image: string;
  isMain?: boolean;
}

const RelatedProducts = () => {
  const { addItem, hasItem } = useCart();
  const color = '#ff2d78';

  // Главный товар + сопутствующие
  const mainProduct: BundleProduct = {
    id: productDetail.id,
    name: productDetail.name,
    price: productDetail.price,
    oldPrice: productDetail.oldPrice,
    rating: productDetail.rating,
    reviews: productDetail.reviewsCount,
    color: productDetail.color,
    description: productDetail.subtitle,
    image: productDetail.images[0],
    isMain: true,
  };

  const allProducts: BundleProduct[] = [mainProduct, ...boughtTogetherProducts];

  // Выбранные товары (по умолчанию — все)
  const [selected, setSelected] = useState<Set<number>>(
    new Set(allProducts.map(p => p.id))
  );
  const [addedAll, setAddedAll] = useState(false);
  const [justAdded, setJustAdded] = useState<Set<number>>(new Set());

  const toggleProduct = (id: number) => {
    if (id === mainProduct.id) return; // главный товар нельзя снять
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setAddedAll(false);
  };

  const selectedProducts = allProducts.filter(p => selected.has(p.id));
  const totalPrice = selectedProducts.reduce((s, p) => s + p.price, 0);
  const totalOld = selectedProducts.reduce((s, p) => s + p.oldPrice, 0);
  const totalSaving = totalOld - totalPrice;
  const savingPct = Math.round((totalSaving / totalOld) * 100);

  const handleAddAll = () => {
    const toAdd = selectedProducts.filter(p => !hasItem(p.id));
    toAdd.forEach(p => {
      addItem({
        id: p.id,
        name: p.name,
        price: p.price,
        oldPrice: p.oldPrice,
        image: p.image,
        color: p.color,
      });
    });
    setAddedAll(true);
    setJustAdded(new Set(toAdd.map(p => p.id)));
    setTimeout(() => {
      setJustAdded(new Set());
    }, 2000);
  };

  // Сбросить состояние addedAll при изменении выбора
  useEffect(() => {
    setAddedAll(false);
  }, [selected]);

  const allAlreadyInCart = selectedProducts.every(p => hasItem(p.id));

  return (
    <section className="py-16 px-4 md:px-8 lg:px-12" style={{ background: '#070710' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                <i className="ri-shopping-basket-2-line" style={{ color, fontSize: '14px' }} />
              </div>
              <h2 className="font-orbitron font-black text-xl md:text-2xl text-white">
                С ЭТИМ ТОВАРОМ ЧАСТО БЕРУТ
              </h2>
            </div>
            <p className="font-mono text-xs text-white/30 pl-8">
              Выберите товары и добавьте всё в корзину одним кликом
            </p>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs text-white/30">
            <i className="ri-checkbox-circle-line" style={{ color: '#39ff14', fontSize: '14px' }} />
            {selected.size} из {allProducts.length} выбрано
          </div>
        </div>

        {/* Products row */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          {allProducts.map((product, idx) => {
            const isSelected = selected.has(product.id);
            const isMain = !!product.isMain;
            const wasJustAdded = justAdded.has(product.id);
            const inCart = hasItem(product.id);

            return (
              <div key={product.id} className="flex flex-col lg:flex-row items-center gap-3 flex-1">
                {/* Product card */}
                <div
                  className="relative rounded-2xl overflow-hidden w-full cursor-pointer transition-all duration-300 group"
                  style={{
                    border: isSelected
                      ? `2px solid ${product.color}`
                      : '2px solid rgba(255,255,255,0.06)',
                    background: isSelected
                      ? `${product.color}06`
                      : 'rgba(255,255,255,0.015)',
                    boxShadow: isSelected ? `0 0 16px ${product.color}20` : 'none',
                    opacity: isSelected ? 1 : 0.45,
                  }}
                  onClick={() => toggleProduct(product.id)}
                >
                  {/* Checkbox */}
                  <div
                    className="absolute top-3 left-3 z-10 w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200"
                    style={{
                      background: isSelected ? product.color : 'rgba(255,255,255,0.08)',
                      border: isSelected ? `2px solid ${product.color}` : '2px solid rgba(255,255,255,0.15)',
                      boxShadow: isSelected ? `0 0 8px ${product.color}60` : 'none',
                    }}
                  >
                    {isSelected && <i className="ri-check-line" style={{ color: '#050508', fontSize: '11px', fontWeight: 900 }} />}
                  </div>

                  {/* Main badge */}
                  {isMain && (
                    <div
                      className="absolute top-3 right-3 z-10 font-mono text-[9px] px-2 py-0.5 rounded-full"
                      style={{ background: `${product.color}20`, color: product.color, border: `1px solid ${product.color}40` }}
                    >
                      Ваш товар
                    </div>
                  )}

                  {/* In cart badge */}
                  {inCart && !isMain && (
                    <div
                      className="absolute top-3 right-3 z-10 font-mono text-[9px] px-2 py-0.5 rounded-full flex items-center gap-1"
                      style={{ background: 'rgba(57,255,20,0.12)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.3)' }}
                    >
                      <i className="ri-check-line text-[9px]" />
                      В корзине
                    </div>
                  )}

                  {/* Image */}
                  <div
                    className="h-36 flex items-center justify-center relative overflow-hidden"
                    style={{ background: `linear-gradient(135deg, #0a0a14 0%, ${product.color}08 100%)` }}
                  >
                    <div
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-12 transition-opacity duration-300"
                      style={{
                        background: product.color,
                        filter: 'blur(18px)',
                        borderRadius: '50%',
                        opacity: isSelected ? 0.18 : 0.06,
                      }}
                    />
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-24 h-24 object-contain relative z-10 transition-transform duration-300 group-hover:scale-105"
                      style={{ filter: `drop-shadow(0 6px 16px ${product.color}${isSelected ? '60' : '25'})` }}
                    />
                    {wasJustAdded && (
                      <div
                        className="absolute inset-0 flex items-center justify-center z-20"
                        style={{ background: `${product.color}20` }}
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ background: product.color, boxShadow: `0 0 20px ${product.color}` }}
                        >
                          <i className="ri-check-line text-lg" style={{ color: '#050508' }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3.5">
                    <div className="flex items-start justify-between gap-1 mb-0.5">
                      <Link
                        to={`/product/${product.id}`}
                        onClick={e => e.stopPropagation()}
                        className="font-orbitron font-bold text-xs leading-tight line-clamp-1 hover:underline transition-colors flex-1"
                        style={{ color: isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)' }}
                      >
                        {product.name}
                      </Link>
                      <Link
                        to={`/product/${product.id}`}
                        onClick={e => e.stopPropagation()}
                        className="w-5 h-5 flex items-center justify-center rounded-md shrink-0 transition-all duration-200 hover:scale-110"
                        style={{ background: `${product.color}15`, border: `1px solid ${product.color}30`, color: product.color }}
                        title="Открыть страницу товара"
                      >
                        <i className="ri-external-link-line" style={{ fontSize: '10px' }} />
                      </Link>
                    </div>
                    <p className="font-mono text-[10px] text-white/35 mb-2 line-clamp-1">{product.description}</p>

                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-2.5">
                      {[1,2,3,4,5].map(s => (
                        <i key={s} className={`text-[9px] ${s <= Math.round(product.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                      ))}
                      <span className="font-mono text-[9px] text-white/30 ml-0.5">{product.reviews}</span>
                    </div>

                    {/* Price */}
                    <div className="flex items-baseline gap-2">
                      <span
                        className="font-orbitron font-bold text-base"
                        style={{ color: isSelected ? product.color : 'rgba(255,255,255,0.3)' }}
                      >
                        {product.price.toLocaleString()} ₽
                      </span>
                      <span className="font-mono text-[10px] text-white/20 line-through">
                        {product.oldPrice.toLocaleString()} ₽
                      </span>
                    </div>
                  </div>
                </div>

                {/* Plus connector */}
                {idx < allProducts.length - 1 && (
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <i className="ri-add-line text-white/30 text-sm" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary + CTA */}
        <div
          className="rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-5"
          style={{
            background: `${color}06`,
            border: `1px solid ${color}25`,
            boxShadow: `0 0 30px ${color}08`,
          }}
        >
          {/* Price summary */}
          <div className="flex-1 w-full">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="font-mono text-xs text-white/40">Итого за</span>
              <span
                className="font-mono text-xs px-2 py-0.5 rounded-full"
                style={{ background: `${color}12`, color, border: `1px solid ${color}30` }}
              >
                {selected.size} {selected.size === 1 ? 'товар' : selected.size <= 4 ? 'товара' : 'товаров'}
              </span>
            </div>

            <div className="flex items-end gap-3 flex-wrap">
              <span
                className="font-orbitron font-black text-3xl"
                style={{ color, textShadow: `0 0 16px ${color}60` }}
              >
                {totalPrice.toLocaleString()} ₽
              </span>
              <span className="font-mono text-sm text-white/25 line-through mb-0.5">
                {totalOld.toLocaleString()} ₽
              </span>
              {totalSaving > 0 && (
                <span
                  className="font-mono text-xs px-2.5 py-1 rounded-full mb-0.5"
                  style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.25)' }}
                >
                  <i className="ri-arrow-down-line mr-1" />
                  Экономия {totalSaving.toLocaleString()} ₽ · {savingPct}%
                </span>
              )}
            </div>

            {/* Per-product breakdown */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {selectedProducts.map(p => (
                <span key={p.id} className="font-mono text-[10px] text-white/25 flex items-center gap-1">
                  <span style={{ color: p.color }}>·</span>
                  {p.name} — {p.price.toLocaleString()} ₽
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-2 w-full sm:w-auto shrink-0">
            <button
              onClick={handleAddAll}
              disabled={selected.size === 0}
              className="whitespace-nowrap flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-orbitron font-bold text-sm tracking-wide cursor-pointer transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
              style={
                allAlreadyInCart || addedAll
                  ? { background: '#39ff14', color: '#050508', boxShadow: '0 0 20px rgba(57,255,20,0.5)' }
                  : { background: color, color: '#050508', boxShadow: `0 0 24px ${color}60` }
              }
            >
              <i className={allAlreadyInCart || addedAll ? 'ri-check-double-line' : 'ri-shopping-bag-line'} />
              {allAlreadyInCart
                ? 'Всё в корзине'
                : addedAll
                ? 'Добавлено!'
                : `Добавить ${selected.size > 1 ? 'всё' : ''} в корзину`}
            </button>

            <button
              onClick={() => {
                // Выбрать все / снять все (кроме главного)
                const nonMain = allProducts.filter(p => !p.isMain).map(p => p.id);
                const allNonMainSelected = nonMain.every(id => selected.has(id));
                setSelected(prev => {
                  const next = new Set(prev);
                  if (allNonMainSelected) {
                    nonMain.forEach(id => next.delete(id));
                  } else {
                    nonMain.forEach(id => next.add(id));
                  }
                  return next;
                });
              }}
              className="whitespace-nowrap font-mono text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer text-center"
            >
              {allProducts.filter(p => !p.isMain).every(p => selected.has(p.id))
                ? 'Снять все дополнительные'
                : 'Выбрать все'}
            </button>
          </div>
        </div>

        {/* Hint */}
        <p className="font-mono text-[10px] text-white/18 text-center mt-3">
          Нажмите на карточку товара, чтобы включить или исключить его из набора
        </p>
      </div>
    </section>
  );
};

export default RelatedProducts;
